import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  CheckCircle2,
  CircleDot,
  ClipboardCopy,
  ExternalLink,
  GitCommitHorizontal,
  MessageSquareQuote,
  Plus,
  Search,
  ShieldCheck,
} from 'lucide-react';
import type {
  TimelineEvent,
  TimelineListResponse,
  TimelineThread,
  TimelineThreadResponse,
  TimelineThreadStatus,
} from '@appcycle/contracts';
import { api, jsonBody } from '../lib/api';
import { useApi } from '../lib/hooks';
import { Button, EmptyState, ErrorState, LoadingState, Modal, PageHeader, StatusLabel } from '../components/core';

const statusTone: Record<TimelineThreadStatus, string> = {
  requested: 'feedback',
  in_progress: 'roadmap',
  implemented: 'changelog',
  verified: 'verified',
  cancelled: 'danger',
};

function ThreadStatus({ value }: { value: TimelineThreadStatus }) {
  return <StatusLabel value={value} tone={statusTone[value]} />;
}

function formatMoment(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function compactRepository(value: string) {
  return value.replace(/^https?:\/\//, '').replace(/\.git$/, '');
}

export function AdminTimelinePage() {
  const [status, setStatus] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const listPath = useMemo(() => {
    const params = new URLSearchParams({ pageSize: '100' });
    if (status) params.set('status', status);
    if (query) params.set('query', query);
    return `/api/v1/timeline?${params}`;
  }, [query, status]);
  const timeline = useApi<TimelineListResponse>(listPath);
  const detail = useApi<TimelineThreadResponse>(selectedId ? `/api/v1/timeline/${encodeURIComponent(selectedId)}` : null);

  useEffect(() => {
    if (!timeline.data) return;
    if (!timeline.data.items.length) {
      setSelectedId(null);
      return;
    }
    const first = timeline.data.items[0];
    if (first && (!selectedId || !timeline.data.items.some((thread) => thread.id === selectedId))) {
      setSelectedId(first.id);
    }
  }, [selectedId, timeline.data]);

  async function requestCreated(threadId: string) {
    setComposerOpen(false);
    await timeline.refresh();
    setSelectedId(threadId);
  }

  return <>
    <PageHeader
      eyebrow="Intent timeline"
      title="See the promise become proof"
      description="User requests, committed implementation, and verification stay on one causal trace."
      action={<Button icon={<Plus size={17} />} onClick={() => setComposerOpen(true)}>Record intent</Button>}
    />
    <TimelineMetrics data={timeline.data} />
    <form className="timeline-toolbar" role="search" onSubmit={(event) => {
      event.preventDefault();
      setQuery(queryInput.trim());
    }}>
      <label className="timeline-search">
        <Search size={15} />
        <span className="sr-only">Search timeline</span>
        <input
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          placeholder="Search requests, commits, or trace IDs"
        />
      </label>
      <label>
        <span className="sr-only">Filter by status</span>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">All states</option>
          <option value="requested">Awaiting implementation</option>
          <option value="in_progress">In progress</option>
          <option value="implemented">Implemented</option>
          <option value="verified">Verified</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </label>
      <Button type="submit" variant="secondary">Find trace</Button>
      {(query || status) && <button type="button" className="text-button" onClick={() => {
        setQuery('');
        setQueryInput('');
        setStatus('');
      }}>Clear</button>}
      <span>{timeline.data?.total ?? 0} traces</span>
    </form>

    {timeline.loading && <LoadingState label="Loading intent timeline" />}
    {timeline.error && <ErrorState message={timeline.error} onRetry={() => void timeline.refresh()} />}
    {timeline.data && !timeline.data.items.length && <EmptyState
      title="No intent traces yet"
      body="Record the first user request here, or let the AppCycle skill create it before implementation starts."
      action={<Button onClick={() => setComposerOpen(true)}>Record first intent</Button>}
    />}
    {timeline.data && timeline.data.items.length > 0 && <div className="timeline-workspace">
      <section className="timeline-thread-list" aria-label="Intent traces">
        <header>
          <span>Trace queue</span>
          <small>Intent → Git → proof</small>
        </header>
        {timeline.data.items.map((thread) => <ThreadRow
          key={thread.id}
          thread={thread}
          selected={selectedId === thread.id}
          onSelect={() => setSelectedId(thread.id)}
        />)}
      </section>
      <section className="timeline-detail" aria-live="polite">
        {detail.loading && <LoadingState label="Loading trace evidence" />}
        {detail.error && <ErrorState message={detail.error} onRetry={() => void detail.refresh()} />}
        {detail.data && <TimelineTrace data={detail.data} />}
      </section>
    </div>}
    <IntentComposer open={composerOpen} onClose={() => setComposerOpen(false)} onCreated={requestCreated} />
  </>;
}

function TimelineMetrics({ data }: { data?: TimelineListResponse }) {
  const counts = data?.counts;
  return <div className="timeline-metrics" aria-label="Timeline coverage">
    <article className="timeline-metric timeline-metric--intent">
      <span><MessageSquareQuote size={16} />Awaiting Git</span>
      <strong>{counts?.awaitingImplementation ?? '—'}</strong>
      <small>User intent without a durable commit</small>
    </article>
    <article className="timeline-metric timeline-metric--implementation">
      <span><GitCommitHorizontal size={16} />Needs proof</span>
      <strong>{counts?.awaitingVerification ?? '—'}</strong>
      <small>Implemented, not yet verified</small>
    </article>
    <article className="timeline-metric timeline-metric--verified">
      <span><ShieldCheck size={16} />Verified</span>
      <strong>{counts?.verified ?? '—'}</strong>
      <small>Implementation and checks connected</small>
    </article>
  </div>;
}

function ThreadRow({ thread, selected, onSelect }: {
  thread: TimelineThread;
  selected: boolean;
  onSelect: () => void;
}) {
  const gap = thread.implementationCount === 0
    ? 'Awaiting Git evidence'
    : thread.verificationCount === 0
      ? 'Verification still open'
      : `${thread.eventCount} evidence events`;
  return <button className={selected ? 'active' : ''} onClick={onSelect}>
    <span className="timeline-thread-list__meta">
      <ThreadStatus value={thread.status} />
      <time>{new Date(thread.updatedAt).toLocaleDateString()}</time>
    </span>
    <strong>{thread.title}</strong>
    <p>{thread.summary || gap}</p>
    <span className="timeline-thread-list__coverage">
      <i className="is-intent" aria-label={`${thread.requestCount} request events`}>{thread.requestCount}</i>
      <span aria-hidden="true" />
      <i className={thread.implementationCount ? 'is-implementation' : ''} aria-label={`${thread.implementationCount} Git implementation events`}>{thread.implementationCount}</i>
      <span aria-hidden="true" />
      <i className={thread.verificationCount ? 'is-verification' : ''} aria-label={`${thread.verificationCount} verification events`}>{thread.verificationCount}</i>
      <em>{gap}</em>
    </span>
  </button>;
}

function TimelineTrace({ data }: { data: TimelineThreadResponse }) {
  const [copied, setCopied] = useState(false);
  async function copyTrace() {
    await navigator.clipboard.writeText(data.thread.correlationKey);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }
  return <>
    <header className="timeline-detail__header">
      <div>
        <span className="timeline-detail__kicker">
          <ThreadStatus value={data.thread.status} />
          <code>{data.thread.correlationKey}</code>
        </span>
        <h2>{data.thread.title}</h2>
        {data.thread.summary && <p>{data.thread.summary}</p>}
      </div>
      <Button variant="secondary" icon={copied ? <CheckCircle2 size={15} /> : <ClipboardCopy size={15} />} onClick={() => void copyTrace()}>
        {copied ? 'Copied' : 'Copy trace ID'}
      </Button>
    </header>
    <div className="timeline-spine">
      {data.events.map((event, index) => <TimelineEventCard
        key={event.id}
        event={event}
        final={index === data.events.length - 1}
      />)}
    </div>
  </>;
}

function TimelineEventCard({ event, final }: { event: TimelineEvent; final: boolean }) {
  const Icon = event.kind === 'request'
    ? MessageSquareQuote
    : event.kind === 'implementation'
      ? GitCommitHorizontal
      : event.kind === 'verification'
        ? ShieldCheck
        : CircleDot;
  const outcome = typeof event.metadata.outcome === 'string' ? event.metadata.outcome : null;
  return <article className={`timeline-event timeline-event--${event.kind}`}>
    <div className="timeline-event__rail">
      <span><Icon size={17} /></span>
      {!final && <i aria-hidden="true" />}
    </div>
    <div className="timeline-event__card">
      <header>
        <span>
          <small>{event.kind} · {event.source}</small>
          <time>{formatMoment(event.occurredAt)}</time>
        </span>
        {outcome && <StatusLabel value={outcome} tone={outcome === 'passed' ? 'verified' : outcome === 'failed' ? 'danger' : 'feedback'} />}
      </header>
      <h3>{event.title}</h3>
      <p>{event.body}</p>
      {event.kind === 'implementation' && event.repository && <div className="timeline-commit">
        <span>
          <GitCommitHorizontal size={14} />
          <strong>{compactRepository(event.repository)}</strong>
        </span>
        {event.branch && <code>{event.branch}</code>}
        {event.commitSha && <code>{event.commitSha.slice(0, 12)}</code>}
        {event.externalUrl && <a href={event.externalUrl} target="_blank" rel="noreferrer">Open commit <ExternalLink size={13} /></a>}
      </div>}
      {event.kind === 'verification' && (typeof event.metadata.command === 'string' || typeof event.metadata.environment === 'string') && <div className="timeline-proof">
        {typeof event.metadata.command === 'string' && <code>{event.metadata.command}</code>}
        {typeof event.metadata.environment === 'string' && <span>{event.metadata.environment}</span>}
        {event.externalUrl && <a href={event.externalUrl} target="_blank" rel="noreferrer">Open evidence <ExternalLink size={13} /></a>}
      </div>}
      {(event.actor || event.artifact) && <footer>
        {event.actor && <span>Recorded by {event.actor.name}</span>}
        {event.artifact && <span>Linked {event.artifact.kind}: {event.artifact.title}</span>}
      </footer>}
    </div>
  </article>;
}

function IntentComposer({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (threadId: string) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    const form = new FormData(event.currentTarget);
    try {
      const response = await api<{ threadId: string }>('/api/v1/timeline/requests', {
        method: 'POST',
        ...jsonBody({
          title: form.get('title'),
          request: form.get('request'),
          summary: form.get('summary'),
          correlationKey: form.get('correlationKey') || undefined,
        }),
      });
      await onCreated(response.threadId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The intent could not be recorded.');
    } finally {
      setSaving(false);
    }
  }
  return <Modal
    open={open}
    onClose={onClose}
    title="Record implementation intent"
    description="Start with what the user asked for. The AppCycle skill will attach Git and verification evidence later."
  >
    <form className="form-stack" onSubmit={(event) => void submit(event)}>
      {error && <p className="form-error" role="alert">{error}</p>}
      <label>Outcome title<input name="title" minLength={4} maxLength={180} required placeholder="Add intent-to-implementation timeline" /></label>
      <label>Original request<textarea name="request" rows={7} required placeholder="What should be implemented? Do not include credentials or secrets." /></label>
      <label>Interpretation<textarea name="summary" rows={3} placeholder="Optional concise description of the intended result" /></label>
      <label>Correlation key <span className="field-optional">optional</span><input className="mono" name="correlationKey" pattern="[a-zA-Z0-9._:-]+" placeholder="task:APPCYCLE-123" /></label>
      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving}>{saving ? 'Recording…' : 'Record intent'}</Button>
      </div>
    </form>
  </Modal>;
}
